import React from 'react';
import { observer } from 'mobx-react';
import { BrowserRouter } from 'react-router-dom';

// Config
import { setToken, config } from '../config';

// Helpers
import { getData, getLocalData, getParam, getUrlSegment, storeData, storeLocalData } from '../helpers';

// Dependencies
import jwt_decode from 'jwt-decode';

// Import common components
import Dialog from './common/Dialog';
import Header from './common/Header';
import Router from './Router';
import Toast from './common/Toast';


class App extends React.Component {
  constructor(props) {
    super(props);

    // Set the initial state
    this.state = {
      render: false
    };

    // Load translation
    const { AppStore } = props.store;

    if (!AppStore.translation) {
      AppStore.getTranslation();
    }
  }

  componentDidMount() {
    const { store, skipTtl } = this.props;
    const { UserStore } = store;
    const { ttlCheckStarted } = UserStore;
    const { adobeScriptSrc, provisionInstall, compassToolAdobeScriptSrc } = config;

    const loggedOut = getUrlSegment(1) === 'session-expired';
    const publicStart = getUrlSegment(1) === 'group';


    // Set document language
    document.documentElement.lang = 'en';

    // Handle token
    let token = getParam('token', window.location) || getData('token') || getLocalData('token');

    if (token && !loggedOut && !publicStart) {
      this.handleToken(token);
    } else if (!loggedOut) {
      this.handleUnauthorized();
    } else if (loggedOut || publicStart) {
      this.setState({
        render: true
      });
    }

    // Start checking token TTL after a minute
    if (!loggedOut && !ttlCheckStarted && !skipTtl) {
      UserStore.ttlCheckStarted = true;

      setTimeout(() => {
        UserStore.checkTtl();
      }, 5000);
    }

    // Aids with button focus styles
    document.querySelector('body').addEventListener('click', (e) => {
      function unClick(e) {
        e.target.classList.remove('btn--clicked');
        e.target.removeEventListener('blur', this);
      }

      if (e.target.className && typeof e.target.className === 'string') {
        if (
          e.target.className.indexOf('btn') > -1 ||
          e.target.className.indexOf('dropdown__trigger') > -1 ||
          e.target.className.indexOf('select') > -1 ||
          e.target.className.indexOf('switch') > -1 ||
          e.target.className.indexOf('sv_select_wrapper') > -1 ||
          e.target.className.indexOf('sv-boolean') > -1
        ) {
          e.target.classList.add('btn--clicked');
          e.target.addEventListener('blur', () => unClick(e));
        } else if (
          e.target.tagName.toLowerCase() === 'select' &&
          (e.target.parentNode.className.indexOf('sv_select_wrapper') > -1 ||
            e.target.parentNode.className.indexOf('select') > -1)
        ) {
          e.target.parentNode.classList.add('btn--clicked');

          e.target.parentNode.addEventListener('blur', (e) => {
            e.target.parentNode.classList.remove('btn--clicked');
          });
        }
      }
    });

    // Get the user’s time zone
    if (!UserStore.timeZone) {
      UserStore.getUserTimeZone();
    }

    const adobeScript = document.createElement('script');
    // This will have to change when other schools start using Public Start but while WGU is the only one
    // this will do the trick
    if (getUrlSegment(1) === 'group' || provisionInstall === UserStore.installSid) {
      adobeScript.src = compassToolAdobeScriptSrc;
    } else {
      adobeScript.src = adobeScriptSrc;
    }
    adobeScript.async = true;

    document.querySelector('#adobe-placeholder').replaceWith(adobeScript);
  }

  /**
   * handleToken - Stores a token and gets initial data
   * @param {string} token
   */
  handleToken = token => {
    const { UserStore } = this.props.store;

    if (!token) {
      return;
    }

    // Add the token to store
    UserStore.token = token;

    // Add the token to session storage
    storeData('token', token);
    storeLocalData('token', token);

    // Add the token to the API header
    setToken(token);

    // Decode the token and store the user data
    let decodedToken = null;

    try {
      decodedToken = jwt_decode(token);
    } catch (error) {
      console.error('Invalid token');
      console.error(error);
    }

    if (!decodedToken || decodedToken.errorCode === '1110') {
      // If the token is unauthorized, handle it
      this.handleUnauthorized(token);
    } else {
      // Otherwise, store the decoded token data
      // and get the initial account data
      UserStore.userData = decodedToken;
      UserStore.installSid = UserStore.userData.installSid;
      UserStore.getInstallSettings();

      storeData('userData', JSON.stringify(decodedToken));

      // If the token has custom stylesheet specified, apply it
      if (decodedToken.cssUrl) {
        // TODO: hook up custom stylesheet handling
        console.log('has custom stylesheet');
      }

      if (
        (decodedToken.role.toLowerCase() === 'learner' ||
          decodedToken.role.toLowerCase() === 'instructor') &&
        !UserStore.signOnData
      ) {
        // Get sign on data
        UserStore.getSignOnData(() => {
          this.setState({
            render: true
          });
        });
      } else {
        this.setState({
          render: true
        });
      }
    }
  };

  /**
   * handleUnauthorized - Redirects unauthorized users
   * @param {string} token
   */
  handleUnauthorized = token => {
    if (
      getUrlSegment(1) !== 'unauthorized' &&
      getUrlSegment(1) !== 'sign-in' &&
      getUrlSegment(1) !== 'group' &&
      getUrlSegment(1) !== 'config-error'
    ) {
      if (token) {
        window.location = `/unauthorized?token=${token}`;
      } else {
        window.location = '/unauthorized';
      }
    }

    this.setState({
      render: true
    });
  };

  render() {
    const { render } = this.state;
    const { AppStore } = this.props.store;

    if (render) {
      return (
        <BrowserRouter>
          <Header {...this.props} />

          <div className="viewport">
            <Router {...this.props} />

            {AppStore.dialog && <Dialog {...this.props} />}

            {AppStore.toast && <Toast {...this.props} />}

            {AppStore.loading && (
              <aside className="loader">
                <div className="loader__msg">
                  <span className="meta">Loading…</span>
                </div>
              </aside>
            )}
          </div>
        </BrowserRouter>
      );
    }

    return null;
  }
}

export default observer(App);
